<script setup>
import { computed, reactive, watch } from 'vue';
import LinkPreviewApi from 'dashboard/api/linkPreview';

const props = defineProps({
  content: { type: String, default: '' },
});

const URL_REGEX = /https?:\/\/[^\s<>"]+/i;

// Cache across all instances so re-renders/scrollback don't refetch the same link.
const previewCache = reactive(new Map());

const url = computed(() => props.content?.match(URL_REGEX)?.[0] || '');

const preview = computed(() => previewCache.get(url.value));

async function loadPreview() {
  if (!url.value || previewCache.has(url.value)) return;

  previewCache.set(url.value, null);
  try {
    const { data } = await LinkPreviewApi.show(url.value);
    if (data?.title || data?.description || data?.image_url) {
      previewCache.set(url.value, data);
    } else {
      previewCache.delete(url.value);
    }
  } catch (error) {
    previewCache.delete(url.value);
  }
}

watch(url, loadPreview, { immediate: true });
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <a
    v-if="preview"
    :href="url"
    target="_blank"
    rel="noopener noreferrer nofollow"
    class="flex flex-col overflow-hidden mt-1 rounded-lg border border-n-weak bg-n-solid-1 hover:bg-n-alpha-1 transition-colors max-w-xs"
  >
    <img
      v-if="preview.image_url"
      :src="preview.image_url"
      class="w-full max-h-40 object-cover"
      alt=""
    />
    <div class="flex flex-col gap-0.5 px-3 py-2">
      <span
        v-if="preview.site_name"
        class="text-xs text-n-slate-10 uppercase tracking-wide"
      >
        {{ preview.site_name }}
      </span>
      <span
        v-if="preview.title"
        class="text-sm font-medium text-n-slate-12 line-clamp-2"
      >
        {{ preview.title }}
      </span>
      <span
        v-if="preview.description"
        class="text-xs text-n-slate-11 line-clamp-2"
      >
        {{ preview.description }}
      </span>
    </div>
  </a>
</template>
